import {
  type ClipboardEvent,
  type KeyboardEvent,
  type ReactElement,
  useCallback,
  useEffect,
  useRef,
} from 'react'

import { cn } from '@/lib/utils'

/**
 * Campo de código de N dígitos (TOTP tem 6).
 *
 * ---------------------------------------------------------------------------
 * COLAGEM É O CASO PRINCIPAL, NÃO O EXÓTICO
 * ---------------------------------------------------------------------------
 * Quase todo mundo copia o código do aplicativo autenticador e cola. Uma
 * implementação ingênua de "uma caixa por dígito" recebe os 6 caracteres na
 * primeira caixa, corta em 1 e joga o resto fora — o usuário cola, vê um dígito
 * só e conclui que o sistema está quebrado. Por isso o `onPaste` é tratado
 * explicitamente aqui: ele distribui os dígitos, foca a última caixa preenchida
 * e dispara `aoCompletar` quando fecha o comprimento.
 *
 * O mesmo vale para o autopreenchimento do SMS/teclado (`autocomplete
 * one-time-code`), que chega como uma inserção única.
 */

const APENAS_DIGITOS = /\D/g

export interface CampoDeCodigoProps {
  readonly valor: string
  readonly aoMudar: (valor: string) => void
  /** Chamado quando o código fica completo (colagem ou último dígito digitado). */
  readonly aoCompletar?: (valor: string) => void
  readonly quantidade?: number
  readonly desabilitado?: boolean
  readonly invalido?: boolean
  /** Rótulo do grupo, para leitor de tela. */
  readonly rotulo: string
  readonly autoFoco?: boolean
}

export function CampoDeCodigo({
  valor,
  aoMudar,
  aoCompletar,
  quantidade = 6,
  desabilitado = false,
  invalido = false,
  rotulo,
  autoFoco = false,
}: CampoDeCodigoProps): ReactElement {
  const referencias = useRef<(HTMLInputElement | null)[]>([])

  const focar = useCallback((indice: number): void => {
    const alvo = referencias.current[indice]
    alvo?.focus()
    alvo?.select()
  }, [])

  const aplicar = useCallback(
    (proximo: string, indiceParaFocar: number): void => {
      const limpo = proximo.replace(APENAS_DIGITOS, '').slice(0, quantidade)
      aoMudar(limpo)
      focar(Math.min(indiceParaFocar, quantidade - 1))

      if (limpo.length === quantidade) {
        aoCompletar?.(limpo)
      }
    },
    [aoCompletar, aoMudar, focar, quantidade],
  )

  function aoDigitar(indice: number, entrada: string): void {
    const digitos = entrada.replace(APENAS_DIGITOS, '')

    if (digitos.length === 0) {
      return
    }

    // Mais de um dígito num campo só = autopreenchimento ou colagem sem evento
    // de paste (acontece no Android). Trata como colagem a partir daqui.
    if (digitos.length > 1) {
      const combinado = `${valor.slice(0, indice)}${digitos}`.slice(0, quantidade)
      aplicar(combinado, combinado.length)
      return
    }

    // Sem "buracos": se a pessoa pula uma caixa, o dígito entra no fim do que
    // já existe. Um código com lacuna no meio nunca seria válido mesmo.
    const caracteres = Array.from({ length: quantidade }, (_, posicao) => valor[posicao] ?? '')
    caracteres[indice] = digitos

    aplicar(caracteres.join(''), indice + 1)
  }

  function aoPressionarTecla(indice: number, evento: KeyboardEvent<HTMLInputElement>): void {
    if (evento.key === 'Backspace') {
      evento.preventDefault()

      if (valor.length > indice) {
        // Apaga o dígito da caixa atual.
        aplicar(`${valor.slice(0, indice)}${valor.slice(indice + 1)}`, indice)
        return
      }

      aplicar(valor.slice(0, Math.max(0, valor.length - 1)), Math.max(0, indice - 1))
      return
    }

    if (evento.key === 'ArrowLeft') {
      evento.preventDefault()
      focar(Math.max(0, indice - 1))
      return
    }

    if (evento.key === 'ArrowRight') {
      evento.preventDefault()
      focar(Math.min(quantidade - 1, indice + 1))
    }
  }

  function aoColar(evento: ClipboardEvent<HTMLInputElement>): void {
    evento.preventDefault()
    const colado = evento.clipboardData.getData('text')
    aplicar(colado, colado.replace(APENAS_DIGITOS, '').length)
  }

  // `autoFocus` como atributo é recusado pelo jsx-a11y (e com razão: ele rouba
  // o foco de quem já estava navegando). Focar no efeito de montagem, só quando
  // a tela existe PARA isso, é o comportamento aceitável.
  useEffect(() => {
    if (autoFoco) {
      referencias.current[0]?.focus()
    }
  }, [autoFoco])

  const caixas = Array.from({ length: quantidade }, (_, indice) => indice)

  return (
    <div role="group" aria-label={rotulo} className="flex justify-between gap-2">
      {caixas.map((indice) => (
        <input
          key={indice}
          ref={(elemento) => {
            referencias.current[indice] = elemento
          }}
          value={valor[indice] ?? ''}
          onChange={(evento) => {
            aoDigitar(indice, evento.target.value)
          }}
          onKeyDown={(evento) => {
            aoPressionarTecla(indice, evento)
          }}
          onPaste={aoColar}
          onFocus={(evento) => {
            evento.target.select()
          }}
          disabled={desabilitado}
          // `inputMode` numérico abre o teclado de números no celular sem
          // impedir a colagem de texto, que `type="number"` atrapalharia.
          inputMode="numeric"
          autoComplete={indice === 0 ? 'one-time-code' : 'off'}
          // O campo aceita mais de um caractere de propósito: é o que permite o
          // autopreenchimento entregar o código inteiro na primeira caixa.
          maxLength={quantidade}
          aria-label={`Dígito ${String(indice + 1)} de ${String(quantidade)}`}
          aria-invalid={invalido ? true : undefined}
          className={cn(
            'h-12 w-full min-w-0 rounded-lg border bg-surface text-center text-lg font-semibold',
            'tabular-money transition-colors focus-visible:outline-none focus-visible:ring-2',
            'focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
            invalido ? 'border-danger-fg' : 'border-border focus-visible:border-border-strong',
          )}
        />
      ))}
    </div>
  )
}
